import { useSupabaseLoader } from "../../../server/supabase/hooks";
import {
  type SegmentWithMoments,
  SupabaseSegmentService,
  SupabaseSegmentsWithMomentsService,
  type SupabaseSegmentWithFavourite,
  SupabaseUsedSegmentInfoService,
  type UsedSegmentInfo,
} from "./supabase-segment-service";

export function useSegmentsWithRelatedMoments({
  is_recipient_type,
  include_deleted = true,
}: {
  is_recipient_type?: boolean;
  include_deleted?: boolean;
} = {}): ReturnType<typeof useSupabaseLoader<SegmentWithMoments[]>> {
  return useSupabaseLoader(
    async ({ supabase, account_id }) => {
      const { data, error } = await new SupabaseSegmentsWithMomentsService(supabase).getAll(
        account_id,
        makeParams({ is_recipient_type, include_deleted }),
      );

      if (error || !data) return [];
      return data;
    },
    [is_recipient_type, include_deleted],
  );
}

export function useSegmentsWithFavourites({
  is_recipient_type,
  include_deleted = true,
}: {
  is_recipient_type: boolean;
  include_deleted?: boolean;
}): ReturnType<typeof useSupabaseLoader<SupabaseSegmentWithFavourite[]>> {
  return useSupabaseLoader(
    async ({ supabase, account_id }) => {
      const { data, error } = await new SupabaseSegmentService(supabase).getAllWithFavoriteSegment(
        account_id,
        makeParams({ is_recipient_type, include_deleted }),
      );

      if (error || !data) return [];
      return data;
    },
    [is_recipient_type, include_deleted],
  );
}

export function useUsedSegmentInfo(): ReturnType<typeof useSupabaseLoader<UsedSegmentInfo[]>> {
  return useSupabaseLoader(async ({ supabase, account_id }) => {
    const { data, error } = await new SupabaseUsedSegmentInfoService(supabase).getAll(account_id, {
      order: [{ column: "id" }],
    });

    if (error || !data) return [];
    return data;
  }, []);
}

const makeParams = ({
  is_recipient_type,
  include_deleted,
}: {
  is_recipient_type?: boolean;
  include_deleted: boolean;
}): {
  order: [{ column: "id" }];
  filter: (
    | { key: "is_recipient_type"; operator: "is"; value: boolean }
    | { key: "is_default"; operator: "not.is"; value: true }
    | { key: "deleted_at"; operator: "is"; value: null }
  )[];
} => ({
  order: [{ column: "id" as const }],
  filter: [
    ...(is_recipient_type !== undefined
      ? [{ key: "is_recipient_type" as const, operator: "is" as const, value: is_recipient_type }]
      : []),
    { key: "is_default" as const, operator: "not.is" as const, value: true },
    ...(include_deleted
      ? []
      : [{ key: "deleted_at" as const, operator: "is" as const, value: null }]),
  ],
});
